import { el } from '../core/dom.js';
import { withProduct } from '../core/route-map.js';
import { formatRelative } from '../core/format.js';
import { icon } from './icons.js';
import { skeleton } from './skeleton.js';
import { empty } from './empty.js';

const WORKSPACE = [
  { id: 'dashboard', href: '/app/dashboard', label: 'Dashboard', icon: 'grid' },
  { id: 'schedules', href: '/app/schedules', label: 'Schedules', icon: 'clock' },
];

/** Owner nav: Settings dropdown stays; Organizations lives only in Settings inner menu. */
const SETTINGS = [
  { id: 'settings', href: '/app/settings', label: 'Workspace', icon: 'people', match: 'settings' },
  { id: 'connectors', href: '/app/connectors', label: 'Connectors', icon: 'plug', match: 'connectors' },
  { id: 'knowledge', href: '/app/knowledge', label: 'Knowledge base', icon: 'book', match: 'knowledge' },
];

function newChatLink(state) {
  return el('a', {
    class: 'new-chat',
    href: withProduct('/app/chat/new', state.productId),
  },
  el('svg', {
    class: 'new-chat-stroke',
    'aria-hidden': 'true',
    preserveAspectRatio: 'none',
  },
  el('rect', { class: 'base', x: '0.5', y: '0.5', width: '100%', height: '100%', fill: 'none', 'stroke-width': '1', pathLength: '100' }),
  el('rect', { class: 'run', x: '0.5', y: '0.5', width: '100%', height: '100%', fill: 'none', 'stroke-width': '1.5', pathLength: '100' }),
  el('rect', { class: 'run2', x: '0.5', y: '0.5', width: '100%', height: '100%', fill: 'none', 'stroke-width': '1.5', pathLength: '100' })),
  el('span', { class: 'new-chat-label' },
    el('span', { class: 'new-chat-plus', 'aria-hidden': 'true' },
      icon('plus', 16),
      el('img', { class: 'new-chat-mark', src: '/site/assets/bonnie-mark-sm.png', alt: '' })),
    'New chat'),
  el('span', { class: 'kbd' }, '⌘N'));
}

function navLink(item, state) {
  const on = state.nav === item.id;
  return el('a', {
    class: on ? 'nav-link is-on' : 'nav-link',
    href: withProduct(item.href, state.productId),
    'aria-current': on ? 'page' : null,
  }, icon(item.icon), el('span', {}, item.label), on ? el('span', { class: 'nav-dot', 'aria-hidden': 'true' }) : null);
}

function subLink(item, state) {
  const on = state.nav === item.match;
  return el('a', {
    class: on ? 'nav-link nav-sub is-on' : 'nav-link nav-sub',
    href: withProduct(item.href, state.productId),
    'aria-current': on ? 'page' : null,
  }, icon(item.icon, 15), el('span', {}, item.label));
}

function chatList(state) {
  if (state.chatsStatus === 'loading') return skeleton(4);
  if (state.chatsStatus === 'error') {
    return empty({ title: 'Chats unavailable', body: 'The chat list could not be loaded.' });
  }
  if (!state.chats.length) {
    return empty({ title: 'No chats yet', body: 'A new chat starts from the button above.' });
  }
  return state.chats.map((chat) => el('a', {
    class: state.chatId && state.chatId === chat.id ? 'chat-link is-on' : 'chat-link',
    href: withProduct(`/app/chat/${encodeURIComponent(chat.id)}`, state.productId),
  },
  el('span', { class: 'chat-title' }, chat.name || 'Chat'),
  el('span', { class: 'chat-time' }, formatRelative(chat.last_message_at))));
}

/**
 * @param {object} state
 * @param {{ onToggleSettings: () => void }} actions
 */
export function renderSidebar(state, actions) {
  const subs = SETTINGS;
  const inGroup = subs.some((item) => item.match === state.nav);
  return el('nav', { class: 'sidebar', 'aria-label': 'Main' },
    el('div', { class: 'side-brand' },
      el('span', { class: 'side-word' },
        el('img', { class: 'side-mark', src: '/site/assets/bonnie-mark-sm.png', alt: '' }),
        el('span', { class: 'side-name' }, 'Bonnie ', el('em', {}, 'Ai'))),
      el('span', { class: 'side-app' }, 'APP')),
    newChatLink(state),
    el('div', { class: 'side-group' },
      el('div', { class: 'side-label' }, 'Workspace'),
      WORKSPACE.map((item) => navLink(item, state)),
      el('div', { class: 'settings-group' },
        el('div', { class: 'settings-row' },
          el('a', {
            class: inGroup && !state.settingsOpen ? 'nav-link is-on' : 'nav-link',
            href: withProduct('/app/settings', state.productId),
          }, icon('sliders'), el('span', {}, 'Settings'), el('span', { class: 'side-count' }, String(subs.length))),
          el('button', {
            type: 'button',
            class: 'icon-btn',
            'aria-expanded': state.settingsOpen ? 'true' : 'false',
            'aria-label': state.settingsOpen ? 'Collapse settings' : 'Expand settings',
            onClick: actions.onToggleSettings,
          }, icon('chevron', 14))),
        state.settingsOpen ? el('div', { class: 'settings-subs' }, subs.map((item) => subLink(item, state))) : null)),
    el('div', { class: 'chat-block' },
      el('div', { class: 'side-label chat-label' },
        el('span', {}, 'Chats'),
        el('span', {}, state.productName || '')),
      el('div', { class: 'chat-list' }, chatList(state))),
    el('div', { class: 'side-foot' },
      el('span', { class: 'side-initial', 'aria-hidden': 'true' }, initial(state.tenantName)),
      el('span', { class: 'side-tenant' },
        el('span', { class: 'side-tenant-name' }, state.tenantName || 'Workspace'),
        el('span', { class: 'side-plan' }, planLine(state.plan)))));
}

function initial(name) {
  const text = String(name || 'B').trim();
  return text ? text.charAt(0).toUpperCase() : 'B';
}

function planLine(plan) {
  const label = String(plan || 'plan').toUpperCase();
  return `${label} · © ${new Date().getFullYear()}`;
}

/** Exported for tests. */
export function settingsNavItems() {
  return SETTINGS.map((item) => ({ label: item.label, href: item.href, match: item.match }));
}
